import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  DocumentData,
  QueryDocumentSnapshot,
  query,
  where,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";
import { compressImageToWebP } from "./postService";
import {
  UserData,
  UserPrivateData,
  UserPublicData,
  FCMTokenData,
  DevicePreferences,
} from "../types/user";
import { getAuth } from "firebase/auth";
// import { functions } from '../firebase/config';
import { ActiveSubscription } from "./stripeTypes"; // adjust import path
import { safeRemoveItem } from "../utils/localStorageUtils";
import { isAbandonedRegistrationOfThisDevice } from "../utils/fcmTokenPrune";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";
import { PUBLIC_USER_FIELDS } from "../constants/userFields";

export type CampaignSignupRow = {
  userId: string;
  username: string;
  displayName: string;
  email?: string;
  signupAt: Date | null;
};

function parseCampaignSignUpsListEntries(
  raw: unknown,
): Array<{ userId: string; at: unknown }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const userId = o.userId;
      if (typeof userId !== "string" || !userId.trim()) return null;
      return { userId: userId.trim(), at: o.at };
    })
    .filter((x): x is { userId: string; at: unknown } => x !== null);
}

export async function getTodayConversation(uid: string): Promise<boolean> {
  if (!uid) return false;

  const conversationsRef = collection(
    db,
    `wizardConversations/${uid}/conversations`,
  );

  const last24Hours = Timestamp.fromMillis(Date.now() - 24 * 60 * 60 * 1000);

  const q = query(conversationsRef, where("createdAt", ">=", last24Hours));

  const snapshot = await getDocs(q);

  return !snapshot.empty;
}

export async function getUserPublicData(
  userId: string,
): Promise<UserPublicData | null> {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return null;
    }
    return userDoc.data() as UserPublicData;
  } catch (error) {
    console.error("Error fetching user public data:", error);
    return null;
  }
}

export class UserService {
  private splitUserData(userData: UserData): {
    publicData: UserPublicData;
    privateData: UserPrivateData;
  } {
    const {
      email,
      dob,
      name,
      surname,
      gender,
      country,
      deviceIDs,
      fcmTokens,
      cookieConsent,
      isEmailVerified,
      ...publicData
    } = userData;

    const privateData: UserPrivateData = {
      email,
      dob,
      name,
      surname,
      gender,
      country,
      deviceIDs: deviceIDs || [],
      fcmTokens: fcmTokens || [],
      ...(cookieConsent && { cookieConsent }),
    };
    // isEmailVerified is intentionally dropped. Only verifyEmailCode (Admin SDK)
    // may set it, and only after the code matches the signed-in account email.
    void isEmailVerified;

    return { publicData, privateData };
  }

  // Helper function to convert Firestore Timestamps to Date objects
  private convertTimestampToDate(timestamp: any): Date | undefined {
    if (!timestamp) return undefined;

    if (timestamp instanceof Date) {
      return timestamp;
    }

    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      return timestamp.toDate();
    }

    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }

    try {
      return new Date(timestamp);
    } catch {
      return undefined;
    }
  }

  private readonly publicFields = PUBLIC_USER_FIELDS;
  private readonly privateFields = [
    "email",
    "dob",
    "name",
    "surname",
    "gender",
    "country",
    "deviceIDs",
    "fcmTokens",
    "cookieConsent",
  ] as const;

  private pickFields<T extends Record<string, any>, K extends keyof T>(
    obj: T,
    fields: readonly K[],
  ): Pick<T, K> {
    const result = {} as Pick<T, K>;
    for (const field of fields) {
      if (obj[field] !== undefined) {
        result[field] = obj[field];
      }
    }
    return result;
  }
  async deactivateUser(userId: string): Promise<void> {
    try {
      await this.updateUserProfile(userId, { deActivated: true });
      console.log(`✅ User ${userId} has been deactivated`);
    } catch (error) {
      console.error(`Error deactivating user ${userId}:`, error);
      throw new Error("Failed to deactivate user");
    }
  }

  /**
   * Deletes the users/{uid} doc and its private/data subdoc. Auth account
   * deletion is handled separately by the caller (firebase/auth deleteUser).
   */
  async deleteUserAccount(userId: string): Promise<void> {
    await deleteDoc(doc(db, "users", userId, "private", "data"));
    await deleteDoc(doc(db, "users", userId));
  }

  /**
   * Reactivate user account
   */
  async activateUser(userId: string): Promise<void> {
    try {
      await this.updateUserProfile(userId, { deActivated: false });
      console.log(`✅ User ${userId} has been reactivated`);
    } catch (error) {
      console.error(`Error reactivating user ${userId}:`, error);
      throw new Error("Failed to reactivate user");
    }
  }
  /**
   * Upload profile image to Firebase Storage.
   * Also generates and uploads normal (512x512) and tiny (64x64) WebP
   * derivative copies to profile-images-webp/{userId}/ for use across the app.
   */
  async uploadProfileImage(
    userId: string,
    imageFile: File,
  ): Promise<{ imageUrl: string; imageUrlNormal: string; imageUrlTiny: string }> {
    try {
      const imageRef = ref(storage, `profile-images/${userId}`);
      await uploadBytes(imageRef, imageFile);
      const imageUrl = await getDownloadURL(imageRef);

      const [normalBlob, tinyBlob] = await Promise.all([
        compressImageToWebP(imageFile, 512, 512, 0.85),
        compressImageToWebP(imageFile, 64, 64, 0.8),
      ]);

      const normalRef = ref(
        storage,
        `profile-images-webp/${userId}/normal.webp`,
      );
      const tinyRef = ref(storage, `profile-images-webp/${userId}/tiny.webp`);

      const [normalSnapshot, tinySnapshot] = await Promise.all([
        uploadBytes(normalRef, normalBlob, { contentType: "image/webp" }),
        uploadBytes(tinyRef, tinyBlob, { contentType: "image/webp" }),
      ]);

      const [imageUrlNormal, imageUrlTiny] = await Promise.all([
        getDownloadURL(normalSnapshot.ref),
        getDownloadURL(tinySnapshot.ref),
      ]);

      return { imageUrl, imageUrlNormal, imageUrlTiny };
    } catch (error) {
      console.error("Error uploading profile image:", error);
      throw new Error("Failed to upload profile image");
    }
  }

  /**
   * Create user document with separated public/private data
   */
  /**
   * Profiles may only carry a username the server has indexed for the user,
   * so a new or changed username is claimed through claimUsername first.
   */
  private async claimUsernameIfChanged(username: unknown, currentUsername: unknown): Promise<void> {
    if (typeof username !== "string" || !username || username === currentUsername) return;
    try {
      await httpsCallable(functions, "claimUsername")({ username });
    } catch (error: any) {
      if (error?.code === "functions/already-exists") throw new Error("Username is already taken");
      if (error?.code === "functions/invalid-argument") throw new Error(error.message);
      throw error;
    }
  }

  async createUserDocument(userId: string, userData: UserData): Promise<void> {
    try {
      const { publicData, privateData } = this.splitUserData(userData);
      const userRef = doc(db, "users", userId);
      const privateRef = doc(db, "users", userId, "private", "data");

      const existing = await getDoc(userRef);
      if (!(existing.exists() && existing.data()?.onboardingComplete === true)) {
        await this.claimUsernameIfChanged(userData.username, existing.data()?.username);
      }
      const publicPayload: DocumentData = { ...publicData };
      delete publicPayload.isEmailVerified;
      // role and isCoach are granted by syncCoachSubscriptionClaim.
      delete publicPayload.role;
      delete publicPayload.isCoach;
      const privatePayload: DocumentData = { ...privateData };
      delete privatePayload.isEmailVerified;
      // Empty defaults would wipe tokens already stored on an existing profile.
      if (!userData.deviceIDs) delete privatePayload.deviceIDs;
      if (!userData.fcmTokens) delete privatePayload.fcmTokens;

      if (existing.exists()) {
        // A finished profile must not be replaced by an onboarding payload.
        if (existing.data()?.onboardingComplete === true) return;
        await setDoc(userRef, publicPayload, { merge: true });
        await setDoc(privateRef, privatePayload, { merge: true });
        return;
      }

      publicPayload.accountCreatedAt = serverTimestamp();
      await setDoc(userRef, publicPayload);
      await setDoc(privateRef, privatePayload);
    } catch (error) {
      console.error("Error creating user document:", error);
      throw error;
    }
  }

  /**
   * Get complete user data by merging public and private data
   */
  // async getUserData(userId: string): Promise<UserData | null> {
  //   try {
  //     // Get public data from main document
  //     const publicDoc = await getDoc(doc(db, "users", userId));
  //     if (!publicDoc.exists()) {
  //       return null;
  //     }
  //     const publicData = publicDoc.data() as UserPublicData;

  //     // Get private data from subcollection
  //     const privateDoc = await getDoc(
  //       doc(db, "users", userId, "private", "data"),
  //     );
  //     if (!privateDoc.exists()) {
  //       return null;
  //     }
  //     const privateData = privateDoc.data() as UserPrivateData;

  //     // Convert Firestore Timestamps to Date objects for FCM tokens
  //     if (privateData.fcmTokens) {
  //       privateData.fcmTokens = privateData.fcmTokens.map((token) => ({
  //         ...token,
  //         createdAt: this.convertTimestampToDate(token.createdAt) || new Date(),
  //         lastUsed: this.convertTimestampToDate(token.lastUsed),
  //       }));
  //     }

  //     // Merge and return complete user data
  //     return { ...publicData, ...privateData } as UserData;
  //   } catch (error) {
  //     console.error("Error getting user data:", error);
  //     throw new Error("Failed to retrieve user data");
  //   }
  // }
  /**
   * Get complete user data by merging public and private data
   */
  async getUserData(userId: string): Promise<UserData | null> {
    try {
      // 1. Get public data from main document (This usually always works)
      const publicDoc = await getDoc(doc(db, "users", userId));
      if (!publicDoc.exists()) {
        return null;
      }
      const publicData = publicDoc.data() as UserPublicData;

      // 2. Try to get private data from subcollection
      let privateData: UserPrivateData | null = null;

      try {
        const privateDoc = await getDoc(
          doc(db, "users", userId, "private", "data"),
        );
        if (privateDoc.exists()) {
          privateData = privateDoc.data() as UserPrivateData;

          // Convert Firestore Timestamps to Date objects for FCM tokens
          if (privateData.fcmTokens) {
            privateData.fcmTokens = privateData.fcmTokens.map((token) => ({
              ...token,
              createdAt:
                this.convertTimestampToDate(token.createdAt) || new Date(),
              lastUsed: this.convertTimestampToDate(token.lastUsed),
            }));
          }
        }
      } catch (privateError) {
        // If we can't read private data (Permission Denied or Not Found),
        // we just log it and continue. We will return only Public Data.
        console.warn(
          `[UserService] Could not fetch private data for user ${userId}. Returning public data only.`,
        );
      }

      // 3. Merge and return. If privateData is null, this just returns publicData
      return { ...publicData, ...privateData } as UserData;
    } catch (error) {
      console.error("Error getting user data:", error);
      throw new Error("Failed to retrieve user data");
    }
  }

  /**
   * Get user subscription data
   */
  async getUserSubscription(
    userId: string,
  ): Promise<ActiveSubscription | null> {
    try {
      const subscriptionDoc = await getDoc(
        doc(db, "activeSubscriptions", userId),
      );

      if (!subscriptionDoc.exists()) {
        return null;
      }

      return subscriptionDoc.data() as ActiveSubscription;
    } catch (error) {
      console.error(
        `[UserService] Error fetching subscription for user ${userId}:`,
        error,
      );
      return null;
    }
  }
  /**
   * Get both user data and subscription data in a single call
   */
  // async getUserProfile(userId: string): Promise<{
  //   userData: UserData | null;
  //   subscription: ActiveSubscription | null;
  // }> {
  //   try {
  //     const [userData, subscription] = await Promise.all([
  //       this.getUserData(userId),
  //       this.getUserSubscription(userId),
  //     ]);

  //     return { userData, subscription };
  //   } catch (error) {
  //     console.error("Error getting user profile:", error);
  //     throw new Error("Failed to retrieve user profile");
  //   }
  // }
  /**
   * Get both user data and subscription data in a single call
   * Uses allSettled to ensure subscription permission errors don't block fetching user data
   */
  async getUserProfile(
    userId: string,
  ): Promise<{
    userData: UserData | null;
    subscription: ActiveSubscription | null;
  }> {
    try {
      // Use allSettled instead of all
      const results = await Promise.allSettled([
        this.getUserData(userId),
        this.getUserSubscription(userId),
      ]);

      // Extract data safely regardless of individual failures
      const userData =
        results[0].status === "fulfilled" ? results[0].value : null;
      const subscription =
        results[1].status === "fulfilled" ? results[1].value : null;

      // Optional: Log only if the subscription failed specifically (to track permission issues without crashing)
      if (results[1].status === "rejected") {
        console.log(
          `[UserService] Info: Subscription data skipped for user ${userId} (likely permission denied).`,
        );
      }

      return { userData, subscription };
    } catch (error) {
      // This block now only catches truly unexpected errors, not the permission denials from above
      console.error("Unexpected error in getUserProfile:", error);
      throw new Error("Failed to retrieve user profile");
    }
  }
  /**
   * Complete user registration with profile data and optional image.
   *
   * This is the single profile write at the end of onboarding: one
   * `users/{uid}` + `users/{uid}/private/data` pair (mirrors iOS
   * `UserService.completeUserRegistration`). The Auth account already exists
   * by the time this runs; nothing else should have created the docs.
   */
  async completeUserRegistration(
    userId: string,
    userData: UserData,
    profileImage?: File,
  ): Promise<void> {
    try {
      let finalUserData = { ...userData };

      // Upload profile image if provided
      if (profileImage) {
        const { imageUrl, imageUrlNormal, imageUrlTiny } =
          await this.uploadProfileImage(userId, profileImage);
        finalUserData = { ...finalUserData, imageUrl, imageUrlNormal, imageUrlTiny };
      }

      await this.createUserDocument(userId, finalUserData);
    } catch (error) {
      console.error("Error completing user registration:", error);
      throw error;
    }
  }

  /**
   * Update user profile data (handles both public and private updates)
   */
  async updateUserProfile(
    userId: string,
    updates: Partial<UserData>,
  ): Promise<void> {
    try {
      // Extract public and private updates with proper typing
      if (updates.username !== undefined) {
        const current = await getDoc(doc(db, "users", userId));
        await this.claimUsernameIfChanged(updates.username, current.data()?.username);
      }
      const publicUpdates = this.pickFields(updates, this.publicFields);
      const privateUpdates = this.pickFields(updates, this.privateFields);

      console.log(
        "UserService.updateUserProfile - Public updates:",
        publicUpdates,
      );
      console.log(
        "UserService.updateUserProfile - Private updates:",
        privateUpdates,
      );

      // Update public data if any public fields are included
      if (Object.keys(publicUpdates).length > 0) {
        console.log("Updating public data in Firestore:", publicUpdates);
        await setDoc(doc(db, "users", userId), publicUpdates, { merge: true });
      }

      // Update private data if any private fields are included
      if (Object.keys(privateUpdates).length > 0) {
        console.log("Updating private data in Firestore:", privateUpdates);
        await setDoc(
          doc(db, "users", userId, "private", "data"),
          privateUpdates,
          { merge: true },
        );
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
      if (error instanceof Error && /username/i.test(error.message)) throw error;
      throw new Error("Failed to update user profile");
    }
  }

  /**
   * Get all users (for admin purposes - use with caution)
   * Uses batching to avoid Firebase rate limits
   */
  async getAllUsers(): Promise<Array<{ id: string; userData: UserData }>> {
    try {
      const publicDocsSnapshot = await getDocs(collection(db, "users"));
      console.log(
        `[UserService] Fetching data for ${publicDocsSnapshot.docs.length} users with batching...`,
      );

      // Process users in batches to avoid rate limits
      const BATCH_SIZE = 500;
      const userIds = publicDocsSnapshot.docs.map((doc) => doc.id);
      const allUsers: Array<{ id: string; userData: UserData } | null> = [];

      for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
        const batch = userIds.slice(i, i + BATCH_SIZE);
        console.log(
          `[UserService] Processing batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(userIds.length / BATCH_SIZE)} (${batch.length} users)`,
        );

        const batchResults = await Promise.all(
          batch.map(async (userId) => {
            try {
              const userData = await this.getUserData(userId);
              return userData ? { id: userId, userData } : null;
            } catch (error) {
              console.error(
                `[UserService] Error fetching user ${userId}:`,
                error,
              );
              return null;
            }
          }),
        );

        allUsers.push(...batchResults);

        if (i + BATCH_SIZE < userIds.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      console.log(
        `[UserService] Successfully fetched ${allUsers.filter((u) => u !== null).length} users`,
      );

      // Filter out null values
      return allUsers.filter(
        (user): user is { id: string; userData: UserData } => user !== null,
      );
    } catch (error) {
      console.error("Error getting all users:", error);
      throw new Error("Failed to retrieve all users");
    }
  }

  /**
   * Get all coaches and athletes with organizations (for admin purposes - use with caution)
   * Fetches both role='coach' and role='athlete' with organisationId
   * Uses batching to avoid Firebase rate limits
   */
  async getAllCoaches(): Promise<Array<{ id: string; userData: UserData }>> {
    try {
      const ref = collection(db, "users");

      // Query 1: Get all coaches
      const coachQuery = query(ref, where("role", "==", "coach"));
      const coachDocsSnapshot = await getDocs(coachQuery);
      console.log(
        `[UserService] Found ${coachDocsSnapshot.docs.length} coaches`,
      );

      // Query 2: Get all users with role = 'Coach' (case-insensitive)
      const coachQueryCI = query(ref, where("role", "==", "Coach"));
      const coachDocsSnapshotCI = await getDocs(coachQueryCI);
      console.log(
        `[UserService] Found ${coachDocsSnapshotCI.docs.length} coaches (case-insensitive)`,
      );

      // Query 3: Get all users with isCoach = true (as a fallback)
      const isCoachQuery = query(ref, where("isCoach", "==", true));
      const isCoachDocsSnapshot = await getDocs(isCoachQuery);
      console.log(
        `[UserService] Found ${isCoachDocsSnapshot.docs.length} coaches (isCoach=true)`,
      );

      // Query 2: Get all athletes with an organisation
      const athleteOrgQuery = query(
        ref,
        where("role", "==", "Athlete"),
        where("organisationId", "!=", null),
      );
      const athleteOrgDocsSnapshot = await getDocs(athleteOrgQuery);
      console.log(
        `[UserService] Found ${athleteOrgDocsSnapshot.docs.length} athletes with organisations`,
      );

      // Combine and deduplicate user IDs
      const userIdSet = new Set<string>();
      coachDocsSnapshot.docs.forEach((doc) => userIdSet.add(doc.id));
      coachDocsSnapshotCI.docs.forEach((doc) => userIdSet.add(doc.id));
      isCoachDocsSnapshot.docs.forEach((doc) => userIdSet.add(doc.id));
      athleteOrgDocsSnapshot.docs.forEach((doc) => userIdSet.add(doc.id));

      const userIds = Array.from(userIdSet);
      console.log(
        `[UserService] Fetching data for ${userIds.length} total users (coaches + athletes with orgs) with batching...`,
      );

      // Process users in batches to avoid rate limits
      const BATCH_SIZE = 500;
      const allUsers: Array<{ id: string; userData: UserData } | null> = [];

      for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
        const batch = userIds.slice(i, i + BATCH_SIZE);
        console.log(
          `[UserService] Processing batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(userIds.length / BATCH_SIZE)} (${batch.length} users)`,
        );

        const batchResults = await Promise.all(
          batch.map(async (userId) => {
            try {
              const userData = await this.getUserData(userId);
              return userData ? { id: userId, userData } : null;
            } catch (error) {
              console.error(
                `[UserService] Error fetching user ${userId}:`,
                error,
              );
              return null;
            }
          }),
        );

        allUsers.push(...batchResults);

        if (i + BATCH_SIZE < userIds.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      console.log(
        `[UserService] Successfully fetched ${allUsers.filter((u) => u !== null).length} users`,
      );

      // Filter out null values
      return allUsers.filter(
        (user): user is { id: string; userData: UserData } => user !== null,
      );
    } catch (error) {
      console.error("Error getting all coaches:", error);
      throw new Error("Failed to retrieve all coaches");
    }
  }

  /**
   * Build one admin signup table row. Pass signupAtOverride undefined to use
   * profile accountCreatedAt; pass Date or null to lock the sign-up column (tracked list).
   */
  private async buildCampaignSignupRow(
    userId: string,
    signupAtOverride: Date | null | undefined,
  ): Promise<CampaignSignupRow> {
    let username = userId;
    let displayName = userId;
    let email: string | undefined;
    let signupAt: Date | null = null;

    try {
      const pubSnap = await getDoc(doc(db, "users", userId));
      if (pubSnap.exists()) {
        const data = pubSnap.data() as UserPublicData & {
          accountCreatedAt?: unknown;
        };
        username = data.username?.trim() || userId;
        displayName = username;
        if (signupAtOverride === undefined) {
          signupAt = this.convertTimestampToDate(data.accountCreatedAt) ?? null;
        } else {
          signupAt = signupAtOverride;
        }
      } else if (signupAtOverride !== undefined) {
        signupAt = signupAtOverride;
      }
    } catch (e) {
      console.error(`[UserService] Public read for signup row ${userId}:`, e);
      if (signupAtOverride !== undefined) {
        signupAt = signupAtOverride;
      }
    }

    try {
      const privateDoc = await getDoc(
        doc(db, "users", userId, "private", "data"),
      );
      if (privateDoc.exists()) {
        const priv = privateDoc.data() as UserPrivateData;
        const name = `${priv.name || ""} ${priv.surname || ""}`.trim();
        if (name) displayName = name;
        email = priv.email;
      }
    } catch (privErr) {
      console.error(
        `[UserService] Private read for signup row ${userId}:`,
        privErr,
      );
    }

    return { userId, username, displayName, email, signupAt };
  }

  /**
   * Get user count for a specific campaign code
   */
  async getUserCountForCampaign(campaignCode: string): Promise<number> {
    try {
      // Query public documents that have the campaign code
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("campaignCode", "==", campaignCode));
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error(
        `Error counting users for campaign ${campaignCode}:`,
        error,
      );
      return 0;
    }
  }

  /**
   * Users who registered with this campaign code (public + private read for admin UI).
   */
  async getUsersForCampaign(
    campaignCode: string,
  ): Promise<CampaignSignupRow[]> {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("campaignCode", "==", campaignCode));
      const querySnapshot = await getDocs(q);

      const rows = await Promise.all(
        querySnapshot.docs.map((d) =>
          this.buildCampaignSignupRow(d.id, undefined),
        ),
      );

      rows.sort((a, b) => {
        const ta = a.signupAt?.getTime() ?? 0;
        const tb = b.signupAt?.getTime() ?? 0;
        return tb - ta;
      });

      return rows;
    } catch (error) {
      console.error(`Error listing users for campaign ${campaignCode}:`, error);
      throw new Error("Failed to load campaign signups");
    }
  }

  /**
   * Resolve signUpsList from a tracked campaign document into table rows.
   */
  async hydrateCampaignSignUpsList(
    rawList: unknown,
  ): Promise<CampaignSignupRow[]> {
    const entries = parseCampaignSignUpsListEntries(rawList);
    const sorted = [...entries].sort((a, b) => {
      const ta = this.convertTimestampToDate(a.at)?.getTime() ?? 0;
      const tb = this.convertTimestampToDate(b.at)?.getTime() ?? 0;
      return tb - ta;
    });

    return Promise.all(
      sorted.map((e) =>
        this.buildCampaignSignupRow(
          e.userId,
          this.convertTimestampToDate(e.at) ?? null,
        ),
      ),
    );
  }

  /**
   * Handle campaign code cleanup
   */
  cleanupCampaignCode(campaignCode?: string): void {
    if (campaignCode) {
      safeRemoveItem("campaignCode");
    }
  }

  /**
   * Get user's FCM tokens
   */
  async getUserFCMTokens(userId: string): Promise<FCMTokenData[]> {
    try {
      const userData = await this.getUserData(userId);
      const tokens = userData?.fcmTokens || [];
      // console.log(`Retrieved ${tokens.length} FCM tokens for user ${userId}`);
      // console.log(
      //   "Retrieved tokens:",
      //   tokens.map((t) => ({
      //     deviceId: t.deviceId,
      //     deviceName: t.deviceName,
      //     enabled: t.enabled,
      //     token: t.token.substring(0, 10) + "...",
      //   })),
      // );
      return tokens;
    } catch (error) {
      console.error("Error getting user FCM tokens:", error);
      throw new Error("Failed to retrieve FCM tokens");
    }
  }

  /**
   * Get all enabled FCM tokens for a user
   */
  async getEnabledFCMTokens(userId: string): Promise<string[]> {
    try {
      const tokens = await this.getUserFCMTokens(userId);
      return tokens
        .filter((tokenData) => tokenData.enabled)
        .map((tokenData) => tokenData.token);
    } catch (error) {
      console.error("Error getting enabled FCM tokens:", error);
      throw new Error("Failed to retrieve enabled FCM tokens");
    }
  }

  /**
   * Add or update FCM token for a user
   */
  async addFCMTokenToUser(
    userId: string,
    tokenData: FCMTokenData,
  ): Promise<void> {
    try {
      const { runTransaction } = await import("firebase/firestore");

      await runTransaction(db, async (transaction) => {
        const privateRef = doc(db, "users", userId, "private", "data");
        const privateDoc = await transaction.get(privateRef);

        if (!privateDoc.exists()) {
          throw new Error("User private data not found");
        }

        const existingTokens: FCMTokenData[] =
          privateDoc.data()?.fcmTokens || [];

        const existingIndex = existingTokens.findIndex(
          (t) =>
            t.token === tokenData.token || t.deviceId === tokenData.deviceId,
        );

        console.log(
          `Checking if token exists for device ${tokenData.deviceId} (${tokenData.deviceName})`,
        );
        console.log(`Existing tokens count: ${existingTokens.length}`);
        console.log(`Found existing token at index: ${existingIndex}`);

        const now = new Date();
        let updatedTokens: FCMTokenData[];
        if (existingIndex >= 0) {
          // Update existing token in place
          updatedTokens = existingTokens.map((t, i) =>
            i === existingIndex
              ? { ...t, ...tokenData, lastUsed: now, lastSeenClient: now }
              : t,
          );
          console.log(
            `Updated existing token for device ${tokenData.deviceName}`,
          );
        } else {
          // Add new token
          updatedTokens = [
            ...existingTokens,
            { ...tokenData, lastUsed: now, lastSeenClient: now },
          ];
          console.log(`Added new token for device ${tokenData.deviceName}`);
        }

        // Drop a leftover row only when its deviceId is the one checking in
        // now and lastSeenClient shows that row has been quiet for 24h+.
        // A missing lastSeenClient is unknown (production tokens predate the
        // field), not 1970. platform + userAgent is shared by every device
        // on that browser version, so it is not this phone and is never a
        // reason to delete. Same token / same deviceId still update in place
        // above; this only removes an extra row of this deviceId.
        const nowMs = now.getTime();
        updatedTokens = updatedTokens.filter((t) => {
          const drop = isAbandonedRegistrationOfThisDevice(
            t,
            tokenData,
            nowMs,
          );
          if (drop) {
            console.log(
              `Pruning stale token for device ${t.deviceName} (same deviceId, no client check-in in 24h+)`,
            );
          }
          return !drop;
        });

        // Single atomic write — no race condition possible
        transaction.set(
          privateRef,
          { fcmTokens: updatedTokens },
          { merge: true },
        );

        console.log(
          `FCM token saved for user ${userId} on device ${tokenData.deviceName}`,
        );
        console.log(`Total FCM tokens: ${updatedTokens.length}`);
      });
    } catch (error) {
      console.error("Error adding FCM token to user:", error);
      throw new Error("Failed to add FCM token");
    }
  }

  /**
   * Remove invalid FCM token from user's list
   */
  async removeInvalidFCMToken(
    userId: string,
    invalidToken: string,
  ): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error("User data not found");
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];

      // Filter out the invalid token
      const updatedTokens = existingTokens.filter(
        (tokenData) => tokenData.token !== invalidToken,
      );

      // Update user's FCM tokens
      await this.updateUserProfile(userId, { fcmTokens: updatedTokens });

      console.log(`Removed invalid FCM token for user ${userId}`);
    } catch (error) {
      console.error("Error removing invalid FCM token:", error);
      throw new Error("Failed to remove invalid FCM token");
    }
  }

  /**
   * Toggle notification enable/disable for a specific device
   */
  async toggleDeviceNotifications(
    userId: string,
    deviceId: string,
    enabled: boolean,
  ): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        throw new Error("User data not found");
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];

      // Find and update the specific device
      const updatedTokens = existingTokens.map((tokenData) =>
        tokenData.deviceId === deviceId
          ? { ...tokenData, enabled, lastUsed: new Date() }
          : tokenData,
      );

      // Update user's FCM tokens
      await this.updateUserProfile(userId, { fcmTokens: updatedTokens });

      console.log(
        `${
          enabled ? "Enabled" : "Disabled"
        } notifications for device ${deviceId}`,
      );
    } catch (error) {
      console.error("Error toggling device notifications:", error);
      throw new Error("Failed to toggle device notifications");
    }
  }

  /**
   * Update last used timestamp for an FCM token
   */
  async updateFCMTokenLastUsed(userId: string, token: string): Promise<void> {
    try {
      const userData = await this.getUserData(userId);
      if (!userData) {
        return; // Silently fail if user not found
      }

      const existingTokens: FCMTokenData[] = userData.fcmTokens || [];

      // Update the last used timestamp for the specific token
      const updatedTokens = existingTokens.map((tokenData) =>
        tokenData.token === token
          ? { ...tokenData, lastUsed: new Date() }
          : tokenData,
      );

      // Only update if we found the token
      if (updatedTokens.some((t) => t.lastUsed && t.token === token)) {
        await this.updateUserProfile(userId, { fcmTokens: updatedTokens });
      }
    } catch (error) {
      console.error("Error updating FCM token last used:", error);
      // Don't throw here as this is a non-critical operation
    }
  }

  /**
   * Check if user can access premium features
   * Returns true if subscription type is premium, platinum, or coach
   */
  async canAccessPremium(userId: string | undefined): Promise<boolean> {
    if (!userId) {
      return false;
    }
    const subscription = await this.getUserSubscription(userId);
    if (!subscription) {
      return false;
    }

    const subscriptionType = subscription.subscriptionType?.toLowerCase();
    return (
      subscriptionType === "premium" ||
      subscriptionType === "platinum" ||
      subscriptionType === "coach"
    );
  }
  async getTodayConversation(uid: string): Promise<boolean> {
    if (!uid) return false;

    const conversationsRef = collection(
      db,
      `wizardConversations/${uid}/conversations`,
    );

    const last24Hours = Timestamp.fromMillis(Date.now() - 24 * 60 * 60 * 1000);

    const q = query(conversationsRef, where("createdAt", ">=", last24Hours));

    const snapshot = await getDocs(q);

    return !snapshot.empty;
  }
  async updateDevicePreferences(
    userId: string,
    preferences: DevicePreferences,
  ): Promise<void> {
    try {
      await this.updateUserProfile(userId, {
        devicePreferences: preferences,
      });
    } catch (error) {
      console.error("Error updating device preferences:", error);
      throw new Error("Failed to update device preferences");
    }
  }
  
async requestAccountDeletion(reason?: string): Promise<void> {
  const fn = httpsCallable(functions, "requestAccountDeletion");
  await fn({ reason: reason || "" });
}

/**
 * Marks the legal deletionRequested record as fulfilled. The doc itself
 * is never deleted — it's the retained trace record — only its status.
 */
async markDeletionFulfilled(uid: string): Promise<void> {
  await setDoc(
    doc(db, "deletionRequested", uid),
    { status: "fulfilled", fulfilledAt: serverTimestamp() },
    { merge: true },
  );
}
}

// Export singleton instance
export const userService = new UserService();
